<template>
  <div class="azcon-chip-swiper" :style="{ '--swiper-gap': gap + 'px' }">
    <button v-show="showNav" class="azcon-chip-swiper__arrow azcon-chip-swiper__arrow--prev" ref="prevBtn">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="15 18 9 12 15 6"/>
      </svg>
    </button>
    <div ref="container" class="swiper-container azcon-chip-swiper__track" @click="onTrackClick">
      <div class="swiper-wrapper">
        <div v-for="(item, index) in items"
             :key="itemKey ? item[itemKey] : index"
             class="swiper-slide"
        >
          <slot :item="item" :index="index"/>
        </div>
      </div>
    </div>
    <button v-show="showNav" class="azcon-chip-swiper__arrow azcon-chip-swiper__arrow--next" ref="nextBtn">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="9 18 15 12 9 6"/>
      </svg>
    </button>
  </div>
</template>

<script>
import { Swiper, Navigation } from "swiper";
import "swiper/swiper-bundle.min.css";

Swiper.use([Navigation]);

export default {
  name: "AzconChipSwiper",
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    itemKey: {
      type: String,
      default: null,
    },
    gap: {
      type: Number,
      default: 6,
    },
  },
  data() {
    return {
      showNav: false,
    };
  },
  methods: {
    init() {
      this.destroy();
      if (!this.$refs.container || !this.items.length) return;
      this.showNav = true;
      this.$nextTick(() => {
        this._swiper = new Swiper(this.$refs.container, {
          slidesPerView: "auto",
          spaceBetween: 0,
          freeMode: true,
          watchOverflow: true,
          navigation: {
            prevEl: this.$refs.prevBtn,
            nextEl: this.$refs.nextBtn,
          },
        });
        this.showNav = !this._swiper.isLocked;
      });
    },
    destroy() {
      if (this._swiper) {
        this._swiper.destroy(true, true);
        this._swiper = null;
      }
    },
    update() {
      if (this._swiper) {
        this._swiper.update();
        this.showNav = !this._swiper.isLocked;
      } else {
        this.init();
      }
    },
    onTrackClick(event) {
      const slide = event.target.closest ? event.target.closest('.swiper-slide') : null;
      const container = this.$refs.container;

      if (!slide || !container || !container.contains(slide) || !this._swiper) return;

      const slideCenter = slide.offsetLeft + (slide.offsetWidth / 2);
      const targetTranslate = -(
        slideCenter - (container.clientWidth / 2)
      );
      const minTranslate = this._swiper.maxTranslate();
      const translate = Math.max(minTranslate, Math.min(0, targetTranslate));

      this._swiper.translateTo(translate, 300, true, true);
    },
  },
  watch: {
    items() {
      this.$nextTick(() => this.update());
    },
  },
  mounted() {
    this.init();
  },
  beforeDestroy() {
    this.destroy();
  },
};
</script>

<style lang="scss" scoped>
.azcon-chip-swiper {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 14px;

  &__track {
    flex: 1;
    min-width: 0;
    overflow: hidden;

    ::v-deep .swiper-wrapper {
      display: flex;
    }

    ::v-deep .swiper-slide {
      width: auto;
      flex-shrink: 0;
      margin-right: var(--swiper-gap, 6px);

      &:last-child {
        margin-right: 0;
      }
    }
  }

  &__arrow {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 1px solid var(--line, #d5dde6);
    background: #fff;
    color: var(--muted, #5f6f82);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.18s ease;
    -webkit-tap-highlight-color: transparent;
    padding: 0;

    &:hover {
      color: var(--text, #1f2a37);
      border-color: #cbd5e1;
      background: #f8fafc;
    }

    &:active {
      transform: scale(0.92);
    }

    &.swiper-button-disabled {
      opacity: 0.35;
      cursor: default;
      pointer-events: none;
    }
  }
}
</style>
